"use client";

import { Book, ChevronRight } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { getMockHelpArticles } from "@/dummy";
import { useVisitorHelpArticlesByIdsQuery } from "@/lib/graphql/queries/generated/help.generated";
import { useConfigLocalizer } from "@/lib/localize-config-text";
import { cacheKeys } from "@/lib/offline-cache";
import { useDemoContent } from "@/lib/use-demo-translator";
import { useOfflineData } from "@/lib/use-offline-data";
import { useWidgetEnv } from "@/lib/use-widget-env";
import type { HomeBlock, WidgetTab } from "@/lib/widget-config";
import { useWidgetInstance } from "@/lib/widget-instance-context";
import { BlockCard } from "../shared";

export function PinnedArticlesBlock({
  block,
  onOpenTab,
  onOpenArticle,
  isDummy,
}: {
  block: Extract<HomeBlock, { kind: "pinned-articles" }>;
  onOpenTab: (tab: WidgetTab) => void;
  /** Tap on a pinned row → drill into the article. Falls back to
   *  opening the help index when undefined. */
  onOpenArticle?: (slug: string) => void;
  isDummy: boolean;
}) {
  const t = useTranslations("home");
  const tDemo = useDemoContent();
  const env = useWidgetEnv();
  const locale = useLocale();
  const localize = useConfigLocalizer();

  // Operator's explicit list IS the answer; honour it up to a sanity
  // cap (10) so a runaway 50-article list doesn't dominate the home tab.
  const ids = (block.articleIds ?? []).slice(0, 10);
  const count = ids.length;

  // Dashboard preview short-circuit — read titles from the operator-
  // prefetched lookup instead of round-tripping through the visitor
  // surface. Production embeds fall through to the real query.
  const previewLookup = !isDummy ? env.previewData?.helpArticlesById : null;
  const usePreview = !!previewLookup;

  // Hydrate real titles via `visitorHelpArticlesByIds(ids)` — BE
  // returns the rows in the same order they were requested, so we
  // can iterate `ids` and look up by index. Demo / preview / empty
  // skip the network. Declared up here (before the demo early-return)
  // so the hook order stays stable across renders.
  const { data } = useVisitorHelpArticlesByIdsQuery({
    variables: { ids, locale },
    skip: isDummy || usePreview || ids.length === 0,
  });
  // Offline READ: cache the pinned articles so they still show on the home tab
  // when the BE is down (keyed by the exact id set).
  const pinnedPk = useWidgetInstance().publicKey;
  const effectiveData = useOfflineData(
    pinnedPk && !isDummy && !usePreview && ids.length > 0
      ? cacheKeys.home(pinnedPk, `help-ids:${ids.join(",")}`, locale)
      : null,
    data,
  );

  // Demo mode: the demo config pins no specific ids, so surface the
  // most-viewed demo help articles as a "Popular articles" list. Real
  // titles + working drill-in, no network.
  if (isDummy) {
    const popular = getMockHelpArticles(tDemo)
      .slice()
      .sort((a, b) => b.viewCount - a.viewCount)
      .slice(0, 4);
    return (
      <BlockCard
        title={localize(block.title, t("popularArticlesTitle"))}
        onOpenAll={() => onOpenTab("help")}
      >
        <ul className="flex flex-col">
          {popular.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                onClick={() => (onOpenArticle ? onOpenArticle(a.slug) : onOpenTab("help"))}
                className="flex w-full items-center gap-2 px-3.5 py-2.5 text-start transition-colors hover:bg-wx-bg-elevated-2"
              >
                <Book size={14} className="shrink-0 text-wx-fg-muted" />
                <span className="min-w-0 flex-1 truncate text-sm text-wx-fg">{a.title}</span>
                <ChevronRight size={14} className="shrink-0 text-wx-fg-subtle rtl:-scale-x-100" />
              </button>
            </li>
          ))}
        </ul>
      </BlockCard>
    );
  }

  if (count === 0) return null;

  // Build an id-keyed lookup. Some ids may resolve to `null` (deleted,
  // unpublished, cross-integration) — those rows fall back to the
  // placeholder copy rather than disappearing, so the operator's
  // pinned-count matches what they configured.
  const titlesById = new Map<string, { title: string; slug: string }>();
  if (usePreview && previewLookup) {
    for (const id of ids) {
      const raw = previewLookup[id];
      if (raw && typeof raw === "object") {
        const r = raw as { title?: unknown; slug?: unknown };
        if (typeof r.title === "string" && typeof r.slug === "string") {
          titlesById.set(id, { title: r.title, slug: r.slug });
        }
      }
    }
  } else {
    for (const row of effectiveData?.visitorHelpArticlesByIds ?? []) {
      titlesById.set(row._id, { title: row.title, slug: row.slug });
    }
  }

  return (
    <BlockCard
      title={localize(block.title, t("pinnedArticlesTitle"))}
      onOpenAll={() => onOpenTab("help")}
    >
      <ul className="flex flex-col">
        {ids.map((id, i) => {
          const hit = titlesById.get(id);
          return (
            <li key={id}>
              <button
                type="button"
                onClick={() => {
                  if (hit && onOpenArticle) onOpenArticle(hit.slug);
                  else onOpenTab("help");
                }}
                className="flex w-full items-center gap-2 px-3.5 py-2.5 text-start transition-colors hover:bg-wx-bg-elevated-2"
              >
                <Book size={14} className="shrink-0 text-wx-fg-muted" />
                <span className="min-w-0 flex-1 truncate text-sm text-wx-fg">
                  {hit ? hit.title : t("pinnedArticleRow", { n: i + 1 })}
                </span>
                <ChevronRight size={14} className="shrink-0 text-wx-fg-subtle rtl:-scale-x-100" />
              </button>
            </li>
          );
        })}
      </ul>
    </BlockCard>
  );
}
